import React, { useState, useEffect } from 'react';
import { Table, Button, Select, Pagination } from 'antd';
import { Eye, Settings } from 'react-feather';
import CreateNewSGP from '../CreateNewSGP/CreateNewSGP';
import SGPDetails from '../SGPDetails/SGPDetails';
import styles from './stylesPopularSPGs.module.css';
import sgpData from './sgpData.json';
import { PlusOutlined } from "@ant-design/icons";

function PopularSPGs() {
    const [upcomingData, setUpcomingData] = useState([]);
    const [liveData, setLiveData] = useState([]);
    const [settledData, setSettledData] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(5);
    const [showCreateSGP, setShowCreateSGP] = useState(false);
    const [showSGPDetails, setShowSGPDetails] = useState(false);

    useEffect(() => {
        setUpcomingData(sgpData.upcomingSGPs);
        setLiveData(sgpData.liveSGPs);
        setSettledData(sgpData.settledSGPs);
    }, []);

    const handlePageChange = (page) => {
        setCurrentPage(page);
    };

    const handlePageSizeChange = (current, size) => {
        setPageSize(size);
        setCurrentPage(1);
    };

    const handleAddSGP = () => {
        setShowCreateSGP(true);
    };

    const handleBackFromCreate = () => {
        setShowCreateSGP(false);
    };

    const handleRowClick = (record) => {
        setShowSGPDetails(true);
    };

    const handleBackFromDetails = () => {
        setShowSGPDetails(false);
    };

    const getColumns = (showOnlyEye = false) => [
        {
            title: 'ID',
            dataIndex: 'id',
            key: 'id',
            width: 120,
            sorter: true,
            render: (text) => (
                <div className="sgp-table-id">
                    <span>{text}</span>
                </div>
            ),
        },
        {
            title: 'Match',
            dataIndex: 'match',
            key: 'match',
            width: 200,
            render: (text) => <span>{text}</span>,
        },
        {
            title: 'Date & Time',
            dataIndex: 'dateTime',
            key: 'dateTime',
            width: 500,
            sorter: true,
            render: (text) => (
                <div className="sgp-table-date">
                    <span>{text}</span>
                </div>
            ),
        },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            width: 100,
            render: (text) => (
                <span className={`sgp-status sgp-status-${text.toLowerCase()}`}>
                    {text === 'Live' && <span className="live-dot">•</span>}
                    {text}
                </span>
            ),
        },
        {
            title: '# Bets',
            dataIndex: 'bets',
            key: 'bets',
            width: 200,
            sorter: true,
            render: (text) => (
                <div className="sgp-table-bets">
                    <span>{text}</span>
                </div>
            ),
        },
        {
            title: 'Staked',
            dataIndex: 'staked',
            key: 'staked',
            width: 150,
            sorter: true,
            render: (text) => (
                <div className="sgp-table-staked">
                    <span>{text}</span>
                </div>
            ),
        },
        {
            title: 'P/L',
            dataIndex: 'pl',
            key: 'pl',
            width: 150,
            sorter: true,
            render: (text) => (
                <div className="sgp-table-pl">
                    <span className={text.startsWith('-') ? 'negative' : text !== '-' ? 'positive' : ''}>
                        {text}
                    </span>
                </div>
            ),
        },
        {
            title: 'Payout',
            dataIndex: 'payout',
            key: 'payout',
            width: 150,
            sorter: true,
            render: (text) => (
                <div className="sgp-table-payout">
                    <span>{text}</span>
                </div>
            ),
        },
        {
            title: 'Market',
            dataIndex: 'market',
            key: 'market',
            width: 250,
            render: (text) => (
                <div className="sgp-table-market">
                    {text.map((line, index) => (
                        <div key={index} className="market-line">
                            {line}
                        </div>
                    ))}
                </div>
            ),
        },
        {
            title: 'Selection',
            dataIndex: 'selection',
            key: 'selection',
            width: 150,
            sorter: true,
            render: (text) => (
                <div className="sgp-table-selection">
                    <div className="selection-items">
                        {text.map((line, index) => (
                            <div key={index} className="selection-line">
                                <span className={`selection-dot ${index === 2 ? 'green' : 'red'}`}>•</span>
                                {line}
                            </div>
                        ))}
                    </div>
                </div>
            ),
        },
        {
            title: 'Odds',
            dataIndex: 'odds',
            key: 'odds',
            width: 100,
            sorter: true,
            render: (text) => (
                <div className="sgp-table-odds">
                    <span>{text}</span>
                </div>
            ),
        },
        {
            title: 'Provider',
            dataIndex: 'provider',
            key: 'provider',
            width: 120,
            sorter: true,
            render: (text) => (
                <div className="sgp-table-provider">
                    <span>{text}</span>
                </div>
            ),
        },
        {
            title: 'Actions',
            key: 'actions',
            width: 100,
            render: (_, record) => (
                <div className="sgp-table-actions">
                    <Button
                        type="text"
                        icon={<Eye size={16} />}
                        className="action-button"
                    />
                    {!showOnlyEye && (
                        <Button
                            type="text"
                            icon={<Settings size={16} />}
                            className="action-button"
                        />
                    )}
                </div>
            ),
        },
    ];

    if (showCreateSGP) {
        return <CreateNewSGP onBack={handleBackFromCreate} />;
    }

    if (showSGPDetails) {
        return <SGPDetails onBack={handleBackFromDetails} />;
    }

    return (
        <div className={styles.container}>
            <div className={styles.popularsgps}>
                {/* Upcoming SGPs Section */}
                <div className={styles.sgpSection}>
                    <div className={styles.sgpSectionHeader}>
                        <h2>Upcoming SGPs</h2>
                        <button
                            type="primary"
                            className={styles.addSgpButton}
                            onClick={handleAddSGP}
                        >
                            <PlusOutlined />
                            Add
                        </button>
                    </div>
                    <div className={styles.sgpTableContainer}>
                        <Table
                            columns={getColumns(false)}
                            dataSource={upcomingData}
                            pagination={false}
                            scroll={{ x: 1500 }}
                            rowKey={(record, index) => `upcoming-${index}`}
                            className="sgp-table"
                            onRow={(record) => ({
                                onClick: () => handleRowClick(record),
                                style: { cursor: 'pointer' }
                            })}
                        />
                    </div>
                </div>

                {/* Live SGPs Section */}
                <div className={styles.sgpSection}>
                    <div className={styles.sgpSectionHeader}>
                        <h2>Live SGPs</h2>
                    </div>
                    <div className={styles.sgpTableContainer}>
                        <Table
                            columns={getColumns(true)}
                            dataSource={liveData}
                            pagination={false}
                            scroll={{ x: 1500 }}
                            rowKey={(record, index) => `live-${index}`}
                            className="sgp-table"
                            onRow={(record) => ({
                                onClick: () => handleRowClick(record),
                                style: { cursor: 'pointer' }
                            })}
                        />
                    </div>
                </div>

                {/* Settled SGPs Section */}
                <div className={styles.sgpSection}>
                    <div className={styles.sgpSectionHeader}>
                        <h2>Settled SGPs</h2>
                    </div>
                    <div className={styles.sgpFilters}>
                        <div className={styles.filterRow}>
                            <div className={styles.filterItem}>
                                <span>Provider</span>
                                <Select placeholder="Not selected" />
                            </div>
                            <div className={styles.filterItem}>
                                <span>Sport</span>
                                <Select placeholder="Not selected" />
                            </div>
                            <div className={styles.filterItem}>
                                <span>League</span>
                                <Select placeholder="Not selected" />
                            </div>
                        </div>
                        <div className={styles.filterRow}>
                            <div className={styles.filterItem}>
                                <span>Match</span>
                                <Select placeholder="Not selected" />
                            </div>
                            <div className={styles.filterItem}>
                                <span>Status</span>
                                <Select placeholder="Not selected" />
                            </div>
                            <div className={styles.filterItem}>
                                <span>Date</span>
                                <Select placeholder="Not selected" />
                            </div>
                        </div>
                        <div className={styles.sgpSelectorActionButtons}>
                            <button className={styles.clear}>Clear Filters</button>
                            <button className={styles.apply}>Apply</button>
                        </div>
                    </div>
                    <div className={styles.sgpTableContainer}>
                        <Table
                            columns={getColumns(true)}
                            dataSource={settledData}
                            pagination={false}
                            scroll={{ x: 1500 }}
                            rowKey={(record, index) => `settled-${index}`}
                            className="sgp-table"
                            onRow={(record) => ({
                                onClick: () => handleRowClick(record),
                                style: { cursor: 'pointer' }
                            })}
                        />
                    </div>
                    <div className={styles.paginationContainer}>
                        <div className={styles.paginationInfo}>
                            <Select
                                value={pageSize}
                                onChange={handlePageSizeChange}
                                style={{ width: 60 }}
                            >
                                <Select.Option value={5}>5</Select.Option>
                                <Select.Option value={10}>10</Select.Option>
                                <Select.Option value={20}>20</Select.Option>
                            </Select>
                            <span>per page of 30,000 events</span>
                        </div>
                        <Pagination
                            current={currentPage}
                            total={30}
                            pageSize={pageSize}
                            showSizeChanger={false}
                            onChange={handlePageChange}
                            itemRender={(page, type, originalElement) => {
                                if (type === 'page') {
                                    return <span className={page === currentPage ? 'active' : ''}>{page}</span>;
                                }
                                return originalElement;
                            }}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
}

export default PopularSPGs;
